import { PHYSICS_EPSILON } from '@md/shared';
import type { Aabb } from './aabb.js';
import type { Body } from './body.js';

/**
 * Vue minimale d'une grille de collision. La physique ne connaît ni les
 * niveaux, ni les jeux : elle ne demande que « cette tuile bloque-t-elle ? ».
 */
export interface CollisionGrid {
  /** Tuile pleine, bloquante dans les quatre directions. */
  isSolidAt(tx: number, ty: number): boolean;
  /** Plateforme fine `_` : bloquante uniquement par le dessus. */
  isOneWayAt(tx: number, ty: number): boolean;
}

/**
 * Obstacle mobile : une boîte solide qui n'appartient pas à la grille de
 * tuiles (porte fermée, bloc métallique, ascenseur…). Le moteur les traite de
 * façon totalement générique — il ignore ce qu'ils représentent.
 */
export interface Obstacle {
  /** Identifiant de l'entité, pour retrouver le support d'un corps porté. */
  readonly id: string;
  readonly box: Aabb;
  /** Ne bloque que par le dessus (plateforme mobile fine). */
  readonly oneWay: boolean;
}

/** Ce qui s'est produit pendant un déplacement résolu. */
export interface MoveResult {
  /** Un mur a stoppé le déplacement horizontal. */
  readonly hitX: boolean;
  /** Le corps s'est posé sur une surface (sol ou plateforme). */
  readonly landed: boolean;
  /** Le corps a heurté un plafond. */
  readonly hitCeiling: boolean;
}

/** Recouvrement STRICT d'un intervalle, avec tolérance numérique. */
function spansOverlap(aMin: number, aMax: number, bMin: number, bMax: number): boolean {
  return aMin < bMax - PHYSICS_EPSILON && aMax > bMin + PHYSICS_EPSILON;
}

/**
 * Première tuile réellement chevauchée par l'intervalle `[min, max)`.
 *
 * L'epsilon évite de considérer comme chevauchée une tuile que le corps ne
 * fait que TOUCHER : un robot posé sur le sol ne doit pas « rentrer » dans la
 * ligne de sol lorsqu'on cherche les murs à sa hauteur, sinon il ne pourrait
 * plus avancer.
 */
function firstOverlappedTile(min: number): number {
  return Math.floor(min + PHYSICS_EPSILON);
}

/** Dernière tuile réellement chevauchée par l'intervalle `[min, max)`. */
function lastOverlappedTile(max: number): number {
  return Math.floor(max - PHYSICS_EPSILON);
}

/**
 * Déplace un corps de (`dx`, `dy`) en résolvant les collisions **axe par axe**
 * (spec §3) : d'abord l'horizontale, puis la verticale. Les vitesses ne sont
 * pas modifiées ici — c'est le rôle de l'intégrateur, qui interprète le
 * `MoveResult`.
 *
 * Hypothèse (garantie par le clamp de vitesse) : `|dx| < 1` et `|dy| < 1`
 * tuile, donc une seule colonne / ligne de contact est possible par axe.
 */
export function moveBody(
  body: Body,
  grid: CollisionGrid,
  dx: number,
  dy: number,
  obstacles: readonly Obstacle[] = [],
): MoveResult {
  let hitX = false;
  let landed = false;
  let hitCeiling = false;

  // --- Axe X -------------------------------------------------------------
  // Le bord d'attaque est arrondi SANS epsilon : un corps qui déborde d'une
  // fraction d'ulp dans un mur est ramené exactement sur sa face, ce qui garde
  // les positions de repos parfaitement propres (x = 1, x + w = 7…).
  body.x += dx;
  if (dx !== 0) {
    const ty0 = firstOverlappedTile(body.y);
    const ty1 = lastOverlappedTile(body.y + body.h);
    if (dx > 0) {
      const tx = Math.floor(body.x + body.w);
      for (let ty = ty0; ty <= ty1; ty += 1) {
        if (grid.isSolidAt(tx, ty)) {
          body.x = tx - body.w;
          hitX = true;
          break;
        }
      }
    } else {
      const tx = Math.floor(body.x);
      for (let ty = ty0; ty <= ty1; ty += 1) {
        if (grid.isSolidAt(tx, ty)) {
          body.x = tx + 1;
          hitX = true;
          break;
        }
      }
    }
  }

  // Obstacles mobiles : mêmes règles, mais sur des boîtes libres (non alignées
  // sur la grille). Un obstacle `oneWay` ne bloque jamais horizontalement.
  if (dx !== 0) {
    for (const obstacle of obstacles) {
      const { box } = obstacle;
      if (obstacle.oneWay || !spansOverlap(body.y, body.y + body.h, box.y, box.y + box.h)) {
        continue;
      }
      if (!spansOverlap(body.x, body.x + body.w, box.x, box.x + box.w)) {
        continue;
      }
      body.x = dx > 0 ? box.x - body.w : box.x + box.w;
      hitX = true;
    }
  }

  // --- Axe Y -------------------------------------------------------------
  const previousBottom = body.y + body.h;
  body.y += dy;
  if (dy > 0) {
    const ty = Math.floor(body.y + body.h);
    const tx0 = firstOverlappedTile(body.x);
    const tx1 = lastOverlappedTile(body.x + body.w);
    for (let tx = tx0; tx <= tx1; tx += 1) {
      // Une plateforme fine n'arrête que si le corps ARRIVE par au-dessus.
      const blocking =
        grid.isSolidAt(tx, ty) ||
        (grid.isOneWayAt(tx, ty) && previousBottom <= ty + PHYSICS_EPSILON);
      if (blocking) {
        body.y = ty - body.h;
        landed = true;
        break;
      }
    }
  } else if (dy < 0) {
    const ty = Math.floor(body.y);
    const tx0 = firstOverlappedTile(body.x);
    const tx1 = lastOverlappedTile(body.x + body.w);
    for (let tx = tx0; tx <= tx1; tx += 1) {
      if (grid.isSolidAt(tx, ty)) {
        body.y = ty + 1;
        hitCeiling = true;
        break;
      }
    }
  }

  if (dy !== 0) {
    for (const obstacle of obstacles) {
      const { box } = obstacle;
      if (!spansOverlap(body.x, body.x + body.w, box.x, box.x + box.w)) {
        continue;
      }
      if (!spansOverlap(body.y, body.y + body.h, box.y, box.y + box.h)) {
        continue;
      }
      if (dy > 0) {
        // Une plateforme fine mobile n'arrête que si le corps ARRIVE de dessus.
        if (obstacle.oneWay && previousBottom > box.y + PHYSICS_EPSILON) {
          continue;
        }
        body.y = box.y - body.h;
        landed = true;
      } else {
        if (obstacle.oneWay) {
          continue;
        }
        body.y = box.y + box.h;
        hitCeiling = true;
      }
    }
  }

  return { hitX, landed, hitCeiling };
}

/**
 * Obstacle solide sur lequel le corps repose exactement (face supérieure au
 * contact de ses pieds, recouvrement horizontal non nul), ou `null`.
 *
 * C'est ce lien qui permet aux ascenseurs et aux blocs de TRANSPORTER un robot.
 */
export function findSupport(
  body: Body,
  obstacles: readonly Obstacle[],
  epsilon: number,
): Obstacle | null {
  const bottom = body.y + body.h;
  for (const obstacle of obstacles) {
    const { box } = obstacle;
    if (Math.abs(box.y - bottom) > epsilon) {
      continue;
    }
    if (spansOverlap(body.x, body.x + body.w, box.x, box.x + box.w)) {
      return obstacle;
    }
  }
  return null;
}
